import React, { useState } from "react";
import ZoomableImage from "components/ZoomableImage";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

interface ExpandableZoomableImageProps {
  url: string;
  alt?: string;
  /** Viewport height of the inline (non-expanded) image. */
  height?: string;
  /** Header text of the expanded overlay (falls back to alt). */
  title?: string;
  /**
   * Source-document hyperlink shown beside the overlay title (patent /
   * target document, whatever its format).
   */
  link?: string;
  /** Text of the hyperlink, e.g. "View Patent" / "View Target". */
  linkLabel?: string;
}

/**
 * ZoomableImage with an expand button (arrows pointing both ends, same icon as
 * the per-feature column view). Expanding opens a large centered overlay where
 * the same pan/pinch-zoom applies; close via the restore icon or a backdrop
 * click.
 */
const ExpandableZoomableImage: React.FC<ExpandableZoomableImageProps> = ({
  url,
  alt = "",
  height,
  title,
  link,
  linkLabel = "View Source",
}) => {
  const [expanded, setExpanded] = useState(false);

  if (!url) return null;

  return (
    <div className="relative">
      <ZoomableImage url={url} alt={alt} height={height} />
      <button
        type="button"
        title="Expand"
        onClick={() => setExpanded(true)}
        className="absolute top-1.5 right-1.5 z-10 p-1 rounded bg-white/90 border border-gray-200 shadow-sm hover:bg-gray-100 text-[#667085]"
        style={{ cursor: "pointer" }}
      >
        <OpenInFullIcon sx={{ fontSize: 15 }} />
      </button>

      {expanded && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center"
          style={{ zIndex: 1200 }}
          onClick={() => setExpanded(false)}
        >
          <div
            className="bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden border-2 border-gray-200"
            style={{ width: "85vw", height: "85vh" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-200">
              <span className="flex items-center gap-3 min-w-0">
                <span className="text-[14px] font-interSemiBold text-[#1A202C] truncate">
                  {title || alt}
                </span>
                {link && (
                  <a
                    href={link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 text-[12px] text-[#1751D0] font-interMedium hover:underline flex-shrink-0"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {linkLabel}
                    <OpenInNewIcon sx={{ fontSize: 13 }} />
                  </a>
                )}
              </span>
              <button
                type="button"
                title="Restore"
                onClick={() => setExpanded(false)}
                className="p-1 rounded hover:bg-gray-100 text-[#667085]"
                style={{ cursor: "pointer" }}
              >
                <CloseFullscreenIcon sx={{ fontSize: 17 }} />
              </button>
            </div>
            <div className="flex-1 p-3 min-h-0">
              <ZoomableImage url={url} alt={alt} height="100%" />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpandableZoomableImage;
